import { dataURLFileLoader } from "@cyntler/react-doc-viewer";
import React, { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { Document, Page, pdfjs } from 'react-pdf';

// Renderer de PDF propio para DocViewer. El de @cyntler/react-doc-viewer crea el Document sin options, asi que pdf.js
// no tiene las 14 fuentes estandar de PDF: si el archivo no las embebe (ej. ZapfDingbats, la de los tildes de las
// casillas), ese texto no se dibuja. Chrome/Acrobat las traen y lo muestran. Mismo origen que el worker del plugin.
const pdfjsBaseUrl = `https://unpkg.com/pdfjs-dist@${pdfjs.version}`;
pdfjs.GlobalWorkerOptions.workerSrc = `${pdfjsBaseUrl}/legacy/build/pdf.worker.min.js`;

// Constante de modulo: react-pdf recarga el documento si cambia la referencia de options.
const documentOptions = { standardFontDataUrl: `${pdfjsBaseUrl}/standard_fonts/` };

// La app carga Font Awesome 4 (public/index.html): clases "fa fa-*".
const ZOOM_STEP = 0.1;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;

export const PdfRenderer = ({ mainState }) => {
    const { t } = useTranslation();
    const [numPages, setNumPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [zoom, setZoom] = useState(1);
    const [paginated, setPaginated] = useState(false);

    const currentDocument = mainState?.currentDocument;

    useEffect(() => {
        setNumPages(0);
        setCurrentPage(1);
    }, [currentDocument]);

    if (!currentDocument?.fileData)
        return null;

    // Ancho disponible del visor, como el plugin original (deja margen para la barra de scroll).
    const pageWidth = Math.max((mainState.rendererRect?.width || 700) - 100, 200);

    const page = pageNumber => (
        <div key={pageNumber} className="pdf-renderer__page">
            <Page pageNumber={pageNumber} width={pageWidth} scale={zoom} loading={t('commons.documentViewer_Loading')} />
        </div>
    );

    return (
        <div className="pdf-renderer" data-testid="pdf-renderer">
            <div className="pdf-renderer__controls">
                {paginated && numPages > 1 && (
                    <>
                        <button type="button" className="btn btn-default btn-sm" title={t('commons.documentViewer_PreviousPage')}
                            disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}>
                            <i className="fa fa-chevron-left" />
                        </button>
                        <span className="pdf-renderer__page-info">
                            {t('commons.documentViewer_PageOf', { current: currentPage, total: numPages })}
                        </span>
                        <button type="button" className="btn btn-default btn-sm" title={t('commons.documentViewer_NextPage')}
                            disabled={currentPage >= numPages} onClick={() => setCurrentPage(p => p + 1)}>
                            <i className="fa fa-chevron-right" />
                        </button>
                    </>
                )}
                <button type="button" className="btn btn-default btn-sm" title={t('commons.documentViewer_ZoomOut')}
                    disabled={zoom <= MIN_ZOOM} onClick={() => setZoom(z => Math.max(MIN_ZOOM, +(z - ZOOM_STEP).toFixed(1)))}>
                    <i className="fa fa-search-minus" />
                </button>
                <button type="button" className="btn btn-default btn-sm" title={t('commons.documentViewer_ZoomIn')}
                    disabled={zoom >= MAX_ZOOM} onClick={() => setZoom(z => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(1)))}>
                    <i className="fa fa-search-plus" />
                </button>
                <button type="button" className="btn btn-default btn-sm" title={t('commons.documentViewer_ZoomReset')}
                    disabled={zoom === 1} onClick={() => setZoom(1)}>
                    <i className="fa fa-undo" />
                </button>
                {numPages > 1 && (
                    <button type="button" className="btn btn-default btn-sm" title={t('commons.documentViewer_TogglePagination')}
                        onClick={() => setPaginated(p => !p)}>
                        <i className={paginated ? "fa fa-files-o" : "fa fa-file-o"} />
                    </button>
                )}
            </div>
            <Document
                file={currentDocument.fileData}
                options={documentOptions}
                onLoadSuccess={({ numPages: total }) => setNumPages(total)}
                loading={t('commons.documentViewer_Loading')}
                error={t('commons.documentViewer_LoadError')}>
                {paginated
                    ? page(currentPage)
                    : Array.from({ length: numPages }, (_, index) => page(index + 1))}
            </Document>
        </div>
    );
};

PdfRenderer.fileTypes = ["pdf", "application/pdf"];
// Mas peso que el renderer de PDF del paquete (0): DocViewer elige el de mayor weight para el tipo de archivo.
PdfRenderer.weight = 1;
PdfRenderer.fileLoader = dataURLFileLoader;
